import { lazy, Suspense, useState, useEffect } from "react";
import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { LanguageProvider } from "@/lib/i18n";
import { CurrencyProvider } from "@/lib/currency";
import { ErrorBoundary } from "@/components/error-boundary";
import { isAuthenticated, getCurrentUser, setCurrentUser, getSmartRedirect, validateSession } from "@/lib/auth";
import HelpButton from "@/components/HelpButton";
import { useWebSocketConnection, useWsQueryInvalidation } from "@/lib/websocket";
import { OnboardingProvider } from "@/components/onboarding/OnboardingProvider";

// Lazy-loaded pages for code splitting
const HomePage = lazy(() => import("@/pages/home"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const Login = lazy(() => import("@/pages/login"));
const PackagesPage = lazy(() => import("@/pages/packages"));
const TutorsPage = lazy(() => import("@/pages/tutors"));
const TutorProfilePage = lazy(() => import("@/pages/tutor-profile"));
const BookingConfirmation = lazy(() => import("@/pages/booking-confirmation"));
const CheckoutPage = lazy(() => import("@/pages/checkout"));
const SubscribePage = lazy(() => import("@/pages/subscribe"));
const ContactPage = lazy(() => import("@/pages/contact"));
const ProfilePage = lazy(() => import("@/pages/profile"));
const SettingsPage = lazy(() => import("@/pages/settings"));
const AIPracticePage = lazy(() => import("@/pages/ai-practice"));
const AdminPage = lazy(() => import("@/pages/admin"));
const CrmArea = lazy(() => import("@/pages/crm"));
const TutorDashboardPage = lazy(() => import("@/pages/tutor-dashboard"));
const TutorAvailabilityPage = lazy(() => import("@/pages/tutor-availability"));
const SupportPage = lazy(() => import("@/pages/support"));
const GuidePage = lazy(() => import("@/pages/guide"));
const MessagesPage = lazy(() => import("@/pages/messages"));
const LearningPathPage = lazy(() => import("@/pages/learning-path"));
const PlacementTestPage = lazy(() => import("@/pages/placement-test"));
const TutorInvitePage = lazy(() => import("@/pages/tutor-invite"));
const TutorAIAssistantPage = lazy(() => import("@/pages/tutor-ai-assistant"));
const ForgotPasswordPage = lazy(() => import("@/pages/forgot-password"));
const ResetPasswordPage = lazy(() => import("@/pages/reset-password"));
const StudyPlanPage = lazy(() => import("@/pages/study-plan"));
const TutorDiagnosticPage = lazy(() => import("@/pages/tutor-diagnostic"));
const TutorStudyPlanReviewPage = lazy(() => import("@/pages/tutor-study-plan-review"));
const NotFound = lazy(() => import("@/pages/not-found"));
const ClassroomPage = lazy(() => import("@/pages/classroom"));
const PreflightCheckPage = lazy(() => import("@/pages/classroom").then(m => ({ default: m.PreflightCheck })));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<"loading" | "authenticated" | "unauthenticated">(
    () => (isAuthenticated() ? "authenticated" : "loading"),
  );

  useEffect(() => {
    if (status === "loading") {
      validateSession().then((user) => {
        setStatus(user ? "authenticated" : "unauthenticated");
      });
    }
  }, []);

  if (status === "loading") return <LoadingFallback />;
  if (status === "unauthenticated") return <Redirect to="/login" />;
  return <>{children}</>;
}

function Router() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Switch>
        <Route path="/login" component={Login} />
        {/* Public by design: the student reaches their plan by capability URL,
            not by logging in. See server/routes/diagnostic.ts. */}
        <Route path="/plan/:token" component={StudyPlanPage} />
        {/* Same page, signed-in variant: the student reaching their own plan
            from the dashboard instead of from the delivery email. */}
        <Route path="/plan">
          <ProtectedRoute>
            <StudyPlanPage />
          </ProtectedRoute>
        </Route>
        <Route path="/forgot-password" component={ForgotPasswordPage} />
        <Route path="/reset-password" component={ResetPasswordPage} />
        <Route path="/home">
          <ProtectedRoute>
            <HomePage />
          </ProtectedRoute>
        </Route>
        <Route path="/dashboard">
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        </Route>
        <Route path="/tutors">
          <ProtectedRoute>
            <TutorsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/tutor/:id">
          <ProtectedRoute>
            <TutorProfilePage />
          </ProtectedRoute>
        </Route>
        <Route path="/booking-confirmation">
          <ProtectedRoute>
            <BookingConfirmation />
          </ProtectedRoute>
        </Route>
        <Route path="/packages">
          <ProtectedRoute>
            <PackagesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/subscription">
          <Redirect to="/packages" />
        </Route>
        <Route path="/checkout" component={CheckoutPage} />
        <Route path="/subscribe" component={SubscribePage} />
        <Route path="/contact" component={ContactPage} />
        <Route path="/profile">
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        </Route>
        <Route path="/settings">
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        </Route>
        <Route path="/ai-practice">
          <ProtectedRoute>
            <AIPracticePage />
          </ProtectedRoute>
        </Route>
        <Route path="/crm">
          <Redirect to="/admin/crm" />
        </Route>
        <Route path="/admin/crm/:rest*">
          <ProtectedRoute>
            <CrmArea />
          </ProtectedRoute>
        </Route>
        <Route path="/admin/crm">
          <ProtectedRoute>
            <CrmArea />
          </ProtectedRoute>
        </Route>
        <Route path="/admin/:tab">
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        </Route>
        <Route path="/admin">
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        </Route>
        <Route path="/support">
          <ProtectedRoute>
            <SupportPage />
          </ProtectedRoute>
        </Route>
        <Route path="/guide">
          <ProtectedRoute>
            <GuidePage />
          </ProtectedRoute>
        </Route>
        <Route path="/messages">
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        </Route>
        <Route path="/classroom/:id/preflight">
          <ProtectedRoute>
            <PreflightCheckPage />
          </ProtectedRoute>
        </Route>
        <Route path="/classroom/:id">
          <ProtectedRoute>
            <ClassroomPage />
          </ProtectedRoute>
        </Route>
        <Route path="/learning-path">
          <ProtectedRoute>
            <LearningPathPage />
          </ProtectedRoute>
        </Route>
        <Route path="/placement-test">
          <ProtectedRoute>
            <PlacementTestPage />
          </ProtectedRoute>
        </Route>
        <Route path="/tutor-portal">
          <ProtectedRoute>
            <TutorDashboardPage />
          </ProtectedRoute>
        </Route>
        {/* The coach's workspace for one diagnostic class: briefing, transcript,
            rubric, plan. Nested under /tutor-portal to match the rest. */}
        <Route path="/tutor-portal/diagnostic/:classId">
          <ProtectedRoute>
            <TutorDiagnosticPage />
          </ProtectedRoute>
        </Route>
        <Route path="/tutor-portal/study-plan/:id">
          <ProtectedRoute>
            <TutorStudyPlanReviewPage />
          </ProtectedRoute>
        </Route>
        <Route path="/tutor-portal/availability">
          <ProtectedRoute>
            <TutorAvailabilityPage />
          </ProtectedRoute>
        </Route>
        <Route path="/tutor-portal/classes">
          <ProtectedRoute>
            <TutorDashboardPage />
          </ProtectedRoute>
        </Route>
        <Route path="/tutor-portal/assistant">
          <ProtectedRoute>
            <TutorAIAssistantPage />
          </ProtectedRoute>
        </Route>
        <Route path="/join">
          <TutorInvitePage />
        </Route>
        <Route path="/">
          <Redirect to={getSmartRedirect(getCurrentUser())} />
        </Route>
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function EmailVerificationBanner() {
  const user = getCurrentUser();
  const [location] = useLocation();
  const [dismissed, setDismissed] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [verified, setVerified] = useState(false);

  // If localStorage says unverified, check server in case it was verified in another tab/redirect
  useEffect(() => {
    if (user && user.emailVerified === false && !user.googleId && !user.microsoftId) {
      fetch("/api/auth/me", { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data?.user?.emailVerified === true) {
            setCurrentUser(data.user);
            setVerified(true);
          }
        })
        .catch(() => {});
    }
  }, []);

  if (!user || dismissed || verified) return null;
  if (user.emailVerified !== false) return null;
  if (user.googleId || user.microsoftId) return null;
  if (location === "/login") return null;

  const handleResend = async () => {
    if (sending || sent) return;
    setSending(true);
    setSendError(false);
    try {
      const res = await fetch("/api/auth/resend-verification", { method: "POST", credentials: "include" });
      if (res.ok) {
        setSent(true);
      } else {
        setSendError(true);
      }
    } catch {
      setSendError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-warning/10 border-b border-warning/30 px-4 py-2 flex items-center justify-between text-sm">
      <span className="text-warning-foreground/90 dark:text-warning">
        Please verify your email address — check your inbox for a verification link.
      </span>
      <div className="flex items-center gap-3 ml-4">
        <button
          onClick={handleResend}
          disabled={sending || sent}
          className="text-warning font-medium underline hover:opacity-80 disabled:opacity-50"
        >
          {sent ? "Email sent!" : sendError ? "Error — try again" : sending ? "Sending…" : "Resend email"}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="text-muted-foreground hover:text-foreground font-medium"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function WebSocketInit() {
  useWebSocketConnection();
  useWsQueryInvalidation();
  return null;
}

function App() {
  const [location] = useLocation();
  return (
    <ErrorBoundary resetKey={location}>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <LanguageProvider>
            <CurrencyProvider>
              <OnboardingProvider>
              <TooltipProvider>
                <WebSocketInit />
                <EmailVerificationBanner />
                <Toaster />
                <Router />
                <HelpButton />
              </TooltipProvider>
              </OnboardingProvider>
            </CurrencyProvider>
          </LanguageProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
